import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { EventPayload } from "./../../common/types/Event";
import {
  customerOrgById,
  IdFilter,
  updateCustomerOrganization,
  runUpdateCustomerCompanyClassification,
} from "./customerAPI";
import {
  UpdateCustomerOrganization,
  UpdateCustomerCompanyClassification,
} from "./types/commands";
import {
  CustomerOrganizationChanged,
  CustomerCompanyClassificationChanged,
} from "./types/events";
import {
  CustomerOrganizationDetails,
  DeliveryMethod,
  InvoiceSettings,
} from "./types/queries";
import { isByEInvoice, isByEmail, isByOperator } from "./companyInfo/helpers";
import { createCommandAsyncThunk } from "../../app/createApiAsyncThunk";

type SliceState = CustomerOrganizationDetails & {
  status: "idle" | "pending" | "succeeded" | "failed";
  error?: string;
};

const initialState: SliceState = {
  id: "",
  customerCompanyId: "",
  customerNumber: "",
  name: "",
  version: 0,
  customerContacts: [],
  industry: "",
  salesPersonId: "",
  salesPersonName: "",
  billingAddress: {
    street: "",
    city: "",
    zip: "",
    country: "",
  },
  businessId: "",
  salesPermission: {
    verificationDate: "",
    riskLevel: undefined,
  },
  invoiceSettings: {
    deliveryMethod: DeliveryMethod.BY_OPERATOR,
  },
  invoiceReferences: undefined,
  invoiceContactPerson: undefined,
  costCenters: [],
  isNationwide: false,
  isRepresentative: false,
  customerCompanyName: undefined,
  status: "idle",
};

export const fetchCustomerOrgById = createAsyncThunk<
  CustomerOrganizationDetails,
  IdFilter,
  { state: RootState }
>(
  "customerOrganization/fetchCustomerOrgById",
  async (filters, { requestId }) => {
    const response = await customerOrgById(filters, {
      requestId,
    });
    return response;
  },
);

export const updateCustomerOrg = createAsyncThunk<
  EventPayload<CustomerOrganizationChanged>[],
  UpdateCustomerOrganization,
  { state: RootState }
>(
  "customerOrganization/updateCustomerOrganization",
  async (organization, { requestId }) => {
    return await updateCustomerOrganization(organization, {
      requestId,
    });
  },
);

export const updateCustomerCompanyClassification = createCommandAsyncThunk<
  UpdateCustomerCompanyClassification,
  CustomerCompanyClassificationChanged
>(
  "customerOrganization/updateCustomerCompanyClassification",
  runUpdateCustomerCompanyClassification,
);

export const customerOrganization = createSlice({
  name: "customerOrganizations",
  initialState,
  reducers: {
    resetCustomerOrganizationState: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCustomerOrgById.fulfilled, (state, action) => {
      state.billingAddress = action.payload.billingAddress;
      state.id = action.payload.id;
      state.customerCompanyId = action.payload.customerCompanyId;
      state.customerNumber = action.payload.customerNumber;
      state.name = action.payload.name;
      state.version = action.payload.version;
      state.customerContacts = action.payload.customerContacts;
      state.industry = action.payload.industry;
      state.salesPersonId = action.payload.salesPersonId;
      state.salesPersonName = action.payload.salesPersonName;
      state.billingAddress = action.payload.billingAddress;
      state.businessId = action.payload.businessId;
      state.salesPermission = action.payload.salesPermission;
      state.invoiceSettings = action.payload.invoiceSettings;
      state.costCenters = action.payload.costCenters;
      state.invoiceReferences = action.payload.invoiceReferences || undefined;
      state.invoiceContactPerson =
        action.payload.invoiceContactPerson || undefined;
      state.isNationwide = action.payload.isNationwide;
      state.isRepresentative = action.payload.isRepresentative;
      state.customerCompanyName = action.payload.customerCompanyName;
      state.status = "succeeded";
    });
    builder.addCase(fetchCustomerOrgById.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchCustomerOrgById.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(updateCustomerOrg.fulfilled, (state, action) => {
      const { salesPersonName } = action.meta.arg;

      action.payload.forEach((event) => {
        switch (event.name) {
          case "orgCusConsChg":
            state.customerContacts = event.payload.customerContacts;
            state.version = event.version;
            break;
          case "orgSalPersonChg":
            state.salesPersonId = event.payload.salesPersonId;
            state.salesPersonName = salesPersonName;
            state.version = event.version;
            break;
          case "orgInvrChg":
            state.invoiceReferences = event.payload.invoiceReferences;
            state.version = event.version;
            break;
          case "orgInvrDel":
            state.invoiceReferences = undefined;
            state.version = event.version;
            break;
          case "orgInvConPerChg":
            state.invoiceContactPerson = event.payload.invoiceContactPerson;
            state.version = event.version;
            break;
          case "orgInvConPerDel":
            state.invoiceContactPerson = undefined;
            state.version = event.version;
            break;
          case "orgInvStngsChg":
            if ("deliveryMethod" in event.payload) {
              if (isByEmail(event.payload)) {
                const updatedSettings: InvoiceSettings = {
                  deliveryMethod: DeliveryMethod.BY_EMAIL,
                  deliveryAddress: {
                    address: event.payload.deliveryAddress.address,
                  },
                };
                state.version = event.version;
                state.invoiceSettings = updatedSettings;
              }

              if (isByEInvoice(event.payload)) {
                const updatedSettings: InvoiceSettings = {
                  deliveryMethod: DeliveryMethod.BY_E_INVOICE,
                  deliveryAddress: {
                    address: event.payload.deliveryAddress.address,
                    operatorId: event.payload.deliveryAddress.operatorId,
                  },
                };
                state.version = event.version;
                state.invoiceSettings = updatedSettings;
              }

              if (isByOperator(event.payload)) {
                const updatedSettings: InvoiceSettings = {
                  deliveryMethod: DeliveryMethod.BY_OPERATOR,
                };
                state.version = event.version;
                state.invoiceSettings = updatedSettings;
              }
            }

            break;
          default:
            break;
        }
      });
      state.status = "succeeded";
    });
    builder.addCase(updateCustomerOrg.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateCustomerOrg.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(
      updateCustomerCompanyClassification.fulfilled,
      (state, action) => {
        action.payload.forEach((event) => {
          switch (event.name) {
            case "cpmClsfnChg":
              state.isNationwide = event.payload.isNationwide;
              state.version = event.version;
              break;
            default:
              break;
          }
        });
        state.status = "succeeded";
      },
    );
    builder.addCase(updateCustomerCompanyClassification.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateCustomerCompanyClassification.rejected, (state) => {
      state.status = "failed";
    });
  },
});

export const { resetCustomerOrganizationState } = customerOrganization.actions;

export const customerOrganizationReducer = customerOrganization.reducer;
